import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from '../lib/supabaseClient'; // adjust path if yours differs

export default function CourseHub() {
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr("");
      const { data, error } = await supabase
        .from("courses")
        .select("id, name, alias")
        .order("name", { ascending: true });

      if (!cancelled) {
        if (error) {
          console.error(error);
          setErr(error.message || "Failed to load courses.");
          setCourses([]);
        } else {
          setCourses(Array.isArray(data) ? data : []);
        }
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return courses;
    return courses.filter(
      (c) =>
        c.name?.toLowerCase().includes(term) ||
        c.alias?.toLowerCase().includes(term)
    );
  }, [courses, q]);

  const copyLink = async (alias) => {
    const url = `/dashboard/${encodeURIComponent(alias)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // no-op
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">🎓 All Courses</h1>

        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or alias…"
            className="border rounded-lg px-3 py-2 w-72"
          />
          <span className="text-sm text-gray-600">
            {filtered.length}/{courses.length}
          </span>
        </div>
      </div>

      {loading && <div>Loading courses…</div>}
      {!loading && err && (
        <div className="text-red-600 border border-red-200 rounded p-3 mb-4">
          {err}
        </div>
      )}

      {!loading && !err && filtered.length === 0 && (
        <div className="text-gray-600">No courses found.</div>
      )}

      {!loading && !err && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((c) => (
            <div
              key={c.id}
              className="border rounded-xl p-4 hover:shadow transition flex flex-col"
            >
              <div className="text-lg font-semibold mb-1">
                {c.name}
              </div>
              <div className="text-sm text-gray-500 mb-4">
                /dashboard/{c.alias}
              </div>

              <div className="mt-auto flex gap-2">
                <Link
                  to={`/dashboard/${encodeURIComponent(c.alias)}`}
                  className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Open
                </Link>
                <button
                  onClick={() => copyLink(c.alias)}
                  className="px-3 py-2 rounded-lg border hover:bg-gray-50"
                  title="Copy link"
                >
                  Copy link
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 text-sm text-gray-600">
        Tip: This list reads from <code>public.courses</code> (columns: <code>id, name, alias</code>)
        and links to <code>/dashboard/&lt;alias&gt;</code>.
      </div>
    </div>
  );
}
